import React, { useState } from 'react';
import { Upload, FileText, Eye, Trash2 } from 'lucide-react';
import { storage, db } from '../../../../firebase';
import { ref, deleteObject } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import { Document, Page, pdfjs } from 'react-pdf';

// Set workerSrc for pdfjs
pdfjs.GlobalWorkerOptions.workerSrc = 
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.8.69/pdf.worker.min.mjs';

const UploadSection = ({
  formData,
  onInputChange,
  uploading,
  error,
  handleFileUpload,
  setError,
  setUploading,
  handlePreview,
  companyId
}) => {
  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });

  const handleDelete = async () => {
    if (!formData.pitchDeck.url || !companyId) return;
    
    try {
      setUploading(true);
      setError(null);

      // Delete from Firebase Storage
      const storageRef = ref(storage, `users/demoUser/pitchDeck/${formData.pitchDeck.fileName}`);
      await deleteObject(storageRef);

      // Update Firestore document
      const pitchDeckDoc = doc(db, 'companies', companyId);
      await updateDoc(pitchDeckDoc, {
        pitchDeck: {
          fileName: '',
          url: '',
          uploaded: false,
          lastUpdated: null,
          slides: 0
        }
      });

      // Update local state
      onInputChange('pitchDeck', 'uploaded', false);
      onInputChange('pitchDeck', 'fileName', '');
      onInputChange('pitchDeck', 'slides', 0);
      onInputChange('pitchDeck', 'lastUpdated', null);
      onInputChange('pitchDeck', 'file', null);
      onInputChange('pitchDeck', 'url', '');

    } catch (error) {
      console.error('Error deleting pitch deck:', error);
      setError('Failed to delete pitch deck. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentLoadSuccess = (pdf) => {
    pdf.getPage(1).then(page => {
      const viewport = page.getViewport({ scale: 1 });
      setPdfDimensions({
        width: viewport.width,
        height: viewport.height
      });
    });
  };

  const getPreviewWidth = () => {
    // Base width for different screen sizes
    const screenWidth = window.innerWidth;
    if (screenWidth < 640) return Math.min(screenWidth - 64, 300); // sm breakpoint
    if (screenWidth < 768) return 320; // md breakpoint
    return 400; // lg and above
  };

  const calculatePreviewHeight = (width) => {
    if (pdfDimensions.width && pdfDimensions.height) {
      const aspectRatio = pdfDimensions.height / pdfDimensions.width;
      return width * aspectRatio;
    }
    // Default aspect ratio for typical slides (4:3 or 16:9)
    return width * (4/3);
  };

  // Only show preview for PDFs
  const isPdf = formData.pitchDeck.fileName && formData.pitchDeck.fileName.toLowerCase().endsWith('.pdf');

  return (
    <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Upload className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">Pitch Deck Upload</h3>
            <p className="text-xs sm:text-sm text-gray-500 truncate">PDF or PowerPoint, max 10MB</p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <label className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium cursor-pointer hover:bg-blue-700 transition-colors w-full sm:w-auto">
            <input
              type="file"
              accept=".pdf,.ppt,.pptx,application/pdf,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
              className="hidden"
              onChange={handleFileUpload}
              disabled={uploading}
            />
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Uploading...' : 'Upload File'}
          </label>
          {formData.pitchDeck.uploaded && (
            <>
              <button
                onClick={handlePreview}
                className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors w-full sm:w-auto"
              >
                <Eye className="w-4 h-4 mr-2" /> Preview
              </button>
              <button
                onClick={handleDelete}
                disabled={uploading}
                className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors w-full sm:w-auto"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete
              </button>
            </>
          )}
        </div>
      </div>
      
      {formData.pitchDeck.uploaded && (
        <>
          <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-2 sm:gap-3">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <span className="text-xs sm:text-sm text-gray-700 truncate">{formData.pitchDeck.fileName}</span>
            <span className="text-xs text-gray-500">{formData.pitchDeck.slides} slides</span>
            <span className="text-xs text-gray-400 ml-auto hidden sm:inline">
              Last updated: {formData.pitchDeck.lastUpdated ? new Date(formData.pitchDeck.lastUpdated).toLocaleString() : ''}
            </span>
          </div>
          
          {/* Show last updated on mobile on new line */}
          <div className="mt-1 sm:hidden">
            <span className="text-xs text-gray-400">
              Last updated: {formData.pitchDeck.lastUpdated ? new Date(formData.pitchDeck.lastUpdated).toLocaleString() : ''}
            </span>
          </div>
          
          {/* PDF First Page Preview */}
          {isPdf && formData.pitchDeck.url && (
            <div className="mt-4 sm:mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Preview</h4>
              <div className="flex justify-center">
                <div 
                  className="relative bg-white border-2 border-gray-200 rounded-lg shadow-md overflow-hidden cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all duration-200 group"
                  onClick={handlePreview}
                  style={{
                    maxWidth: '100%',
                    width: 'fit-content'
                  }}
                >
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 z-10 flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-3">
                      <Eye className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  
                  <Document 
                    file={formData.pitchDeck.url} 
                    onLoadSuccess={handleDocumentLoadSuccess}
                    loading={
                      <div className="flex items-center justify-center bg-gray-50 rounded-lg" 
                           style={{ 
                             width: getPreviewWidth(), 
                             height: calculatePreviewHeight(getPreviewWidth()) 
                           }}>
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                          <div className="text-sm text-gray-500">Loading preview...</div>
                        </div>
                      </div>
                    }
                    error={
                      <div className="flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300" 
                           style={{ 
                             width: getPreviewWidth(), 
                             height: calculatePreviewHeight(getPreviewWidth()) 
                           }}>
                        <div className="text-center text-gray-500 p-4">
                          <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <div className="text-sm">Preview unavailable</div>
                          <div className="text-xs mt-1">Click to open full PDF</div>
                        </div>
                      </div>
                    }
                  >
                    <Page 
                      pageNumber={1} 
                      width={getPreviewWidth()}
                      renderTextLayer={false} 
                      renderAnnotationLayer={false}
                      className="shadow-sm"
                    />
                  </Document>
                  
                  {/* Click hint */}
                  <div className="absolute bottom-2 right-2 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    Click to open
                  </div>
                </div>
              </div>
              
              {/* Mobile-friendly description */}
              <div className="mt-3 text-center">
                <p className="text-xs text-gray-500">
                  Tap preview to open full presentation
                </p>
              </div>
            </div>
          )}
        </>
      )}
      
      {error && (
        <div className="mt-2 sm:mt-3 text-xs sm:text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
          {error}
        </div>
      )}
    </div>
  );
};

export default UploadSection;